import { useHistory } from "react-router-dom";
import { useTranslation, type TFunction } from "react-i18next";
import {
  useAudioContext,
  type AudioMediaDataModel,
} from "../../../domains/app/contexts/audio.context";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import {
  useDesktopArticleListening,
  UP_NEXT_MS,
  type ListenableArticle,
} from "../../../features/article/hooks/useDesktopArticleListening";
import { useNowReadingSurface } from "../../../features/article/hooks/useNowReading";
import { formatTime } from "../../../tools/utils";
import { deriveTrackSource, SPEED_RATES } from "../../media/MiniAudioPlayer";
import {
  SidebarAudioPlayer,
  SidebarAudioRail,
  UpNextCountdown,
  type SidebarAudioPlayerView,
  type SidebarAudioRailView,
} from "./SidebarAudioPlayer";
import type { DesktopSidebarState } from "./DesktopSidebar";

const SKIP_SECONDS = 15;

function articleSource(article: ListenableArticle, t: TFunction): string {
  return article.journal === null
    ? t("Audio summary")
    : t("Audio summary · {{journal}}", { journal: article.journal.name });
}

function mediaSource(
  media: AudioMediaDataModel,
  collectionName: string,
  t: TFunction
): string {
  switch (media.type) {
    case "article_audio":
      return articleSource(media.source, t);
    case "podcast_episode":
    case "playlist_audio_content":
      return deriveTrackSource(media, collectionName);
    default:
      throw new Error(media satisfies never);
  }
}

/**
 * The listening session in the sidebar: the full player when the sidebar is
 * open, the ring on its rail when it is collapsed. Null when nothing plays.
 */
export function AppSidebarAudioPlayer({
  sidebar,
  onExpandSidebar,
}: {
  sidebar: DesktopSidebarState;
  onExpandSidebar: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { articleLink } = usePageLinks(null);
  const listening = useDesktopArticleListening();
  const nowReading = useNowReadingSurface();
  const {
    currentTime,
    duration,
    canSeek,
    isPlaying,
    playbackRate,
    trackCollectionName,
    setPlaybackRate,
  } = useAudioContext();

  const session = listening.sidebar;
  if (session.kind === "none") return null;

  function openArticle(article: ListenableArticle): void {
    history.push(articleLink(article));
  }

  // `countdown` comes from the Up next countdown, which only that state
  // mounts.
  const view = (
    countdown: { secondsLeft: number; waited: number } | null
  ): SidebarAudioPlayerView => {
    switch (session.kind) {
      case "preparing":
        return {
          kind: "preparing",
          title: session.article.title,
          source: articleSource(session.article, t),
          onOpen: () => {
            openArticle(session.article);
          },
          onClose: listening.close,
        };
      case "failed":
        return {
          kind: "failed",
          title: session.article.title,
          source: articleSource(session.article, t),
          onOpen: () => {
            openArticle(session.article);
          },
          onRetry: listening.retry,
          onClose: listening.close,
        };
      case "up-next": {
        if (countdown === null) {
          throw new Error("should not happen: Up next without its countdown");
        }
        const position = t("{{position}} of {{total}}", {
          position: session.position,
          total: session.total,
        });
        return {
          kind: "up-next",
          title: session.article.title,
          source:
            session.article.journal === null
              ? position
              : `${position} · ${session.article.journal.name}`,
          onOpen: () => {
            openArticle(session.article);
          },
          secondsLeft: countdown.secondsLeft,
          waited: countdown.waited,
          onPlayNow: listening.playNext,
          onCancel: listening.cancelUpNext,
        };
      }
      case "track": {
        const media = session.media;
        const isArticle = media.type === "article_audio";
        // The passage link belongs to the article page of the track only.
        const surface =
          isArticle && nowReading !== null
            ? nowReading.articleId === media.source.id
              ? nowReading
              : null
            : null;
        return {
          kind: "track",
          title: media.title,
          source: mediaSource(media, trackCollectionName, t),
          onOpen:
            surface !== null
              ? surface.reveal
              : media.type === "article_audio"
                ? () => {
                    openArticle(media.source);
                  }
                : null,
          isPlaying,
          nowReading:
            surface === null
              ? null
              : {
                  title: surface.title,
                  position: surface.position,
                  onReveal: surface.reveal,
                },
          elapsed: currentTime,
          duration: canSeek && duration > 0 ? duration : null,
          speed: playbackRate,
          onPlayPause: session.onPlayPause,
          onSeek: canSeek ? session.onSeek : null,
          onSkipBack: canSeek
            ? () => {
                // Through the session, like a seek: skipping back from the
                // end of an article makes it the loaded one again.
                session.onSeek(Math.max(0, currentTime - SKIP_SECONDS));
              }
            : null,
          onSkipForward: canSeek
            ? () => {
                session.onSeek(Math.min(duration, currentTime + SKIP_SECONDS));
              }
            : null,
          onCycleSpeed: () => {
            const index = SPEED_RATES.findIndex(
              (rate) => rate === playbackRate
            );
            const next = SPEED_RATES[(index + 1) % SPEED_RATES.length];
            if (next === undefined) {
              throw new Error("should not happen: no playback speed");
            }
            setPlaybackRate(next);
          },
          onNext: session.hasNext ? listening.playNext : null,
          onClose: listening.close,
        };
      }
      default:
        throw new Error(session satisfies never);
    }
  };

  function render(shown: SidebarAudioPlayerView): React.JSX.Element {
    switch (sidebar) {
      case "expanded":
        return <SidebarAudioPlayer view={shown} />;
      case "collapsed":
        return (
          <SidebarAudioRail
            view={railView(
              shown,
              t,
              onExpandSidebar,
              session.kind === "track" && session.media.type === "article_audio"
            )}
          />
        );
      default:
        throw new Error(sidebar satisfies never);
    }
  }

  if (session.kind === "up-next") {
    return (
      <UpNextCountdown
        endsAt={session.endsAt}
        totalMs={UP_NEXT_MS}
        onDone={listening.playNext}
      >
        {(countdown) => render(view(countdown))}
      </UpNextCountdown>
    );
  }
  return render(view(null));
}

function railView(
  view: SidebarAudioPlayerView,
  t: TFunction,
  onExpand: () => void,
  /** The track is an article's audio summary, not a podcast or playlist. */
  isNarration: boolean
): SidebarAudioRailView {
  switch (view.kind) {
    case "track": {
      const left =
        view.duration === null
          ? null
          : formatTime(Math.max(0, view.duration - view.elapsed));
      return {
        title: view.title,
        subtitle:
          left === null || !isNarration
            ? view.source
            : t("Audio summary · {{time}} left", { time: left }),
        control: {
          kind: "track",
          isPlaying: view.isPlaying,
          onPlayPause: view.onPlayPause,
        },
        progress:
          view.duration === null
            ? null
            : Math.min(1, view.elapsed / view.duration),
        onExpand,
      };
    }
    case "preparing":
      return {
        title: view.title,
        subtitle: t("Preparing audio…"),
        control: { kind: "preparing" },
        progress: null,
        onExpand,
      };
    case "failed":
      return {
        title: view.title,
        subtitle: t("Couldn't play the audio"),
        control: { kind: "attention" },
        progress: null,
        onExpand,
      };
    case "up-next":
      return {
        title: view.title,
        subtitle: t("Up next"),
        control: { kind: "attention" },
        progress: null,
        onExpand,
      };
    default:
      throw new Error(view satisfies never);
  }
}
